<template>
  <div>
    <div class="flow-block flow-block--intro mb-4">
      <h4 class="flow-title">Etapa final: conteúdo e validação</h4>
      <p class="flow-subtitle mb-0">Configure o conteúdo da campanha e rode a Pré-validação antes de executar o lote.</p>
      <div v-if="dryRunBlockingReasons.length" class="validation-box mt-3">
        <div class="validation-title">Ajustes pendentes:</div>
        <ul class="validation-list">
          <li v-for="reason in dryRunBlockingReasons" :key="reason">{{ reason }}</li>
        </ul>
      </div>
    </div>

    <div class="flow-block flow-block--step" data-step="1" v-if="!form.useChatFlow">
      <h4 class="flow-title">1. Conteúdo da mensagem</h4>
      <p class="flow-subtitle">Defina texto, primeira mensagem (se humanizada) e mídia.</p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">
            Mensagem {{ isNonOfficialWithoutFlow ? '(obrigatória quando não houver mídia)' : '(opcional)' }}
          </label>
          <vs-textarea v-model="form.message" class="w-full message-textarea" />
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4" v-if="form.isHumanized">
          <label class="input-label">Primeira Mensagem (obrigatória na humanização web)</label>
          <vs-input v-model="form.firstMessage" class="w-full" />
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">
            Upload de Arquivo
            {{ mediaUploadRequired ? `(obrigatório para cabeçalho ${requiredTemplateHeaderFormat})` : '(opcional)' }}
          </label>
          <upload
            :key="`mass-media-upload-${requiredTemplateHeaderFormat || 'default'}`"
            ref="mediaUpload"
            :action="uploadApiUrl"
            fileName="file"
            :headers="uploadHeaders"
            :data="uploadData"
            automatic
            :limit="1"
            :single-upload="true"
            :text="uploadButtonText"
            :accept="uploadAccept"
            @on-success="successUploadMedia"
            @on-error="errorUploadMedia"
            @on-delete="deleteUploadMedia"
          />

          <div class="uploaded-media-info mt-2" v-if="trimmedImageUrl">
            <vs-button
              size="small"
              color="danger"
              type="border"
              icon-pack="feather"
              icon="icon-trash"
              class="media-remove-button"
              @click="removeUploadedMedia"
            />
            <p class="field-hint mb-0">
              Arquivo carregado: {{ form.imageCaption || 'mídia anexada' }}
            </p>
          </div>

          <vs-alert
            color="warning"
            icon="new_releases"
            active
            class="mt-2 text-sm"
            v-if="mediaUploadRequired && !form.imageUrl"
          >
            O modelo selecionado exige mídia no cabeçalho.
          </vs-alert>

          <vs-alert
            color="warning"
            icon="new_releases"
            active
            class="mt-2 text-sm"
            v-else-if="mediaUploadRequired && isHeaderMediaIncompatible"
          >
            Mídia incompatível. O modelo exige {{ requiredTemplateHeaderFormat }} e o arquivo enviado é {{ currentImageFormat || '-' }}.
          </vs-alert>

          <div class="media-preview mt-2" v-if="form.imageUrl && !isHeaderMediaIncompatible">
            <img
              v-if="currentImageFormat === 'IMAGE'"
              :src="form.imageUrl"
              alt="Pré-visualização da mídia"
            >
            <video
              v-else-if="currentImageFormat === 'VIDEO'"
              :src="form.imageUrl"
              controls
            />
            <a
              v-else
              :href="form.imageUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir arquivo enviado
            </a>
          </div>

          <div class="vx-col mt-5 w-full md:w-1/2 mb-4" v-if="trimmedImageUrl">
            <label class="input-label">Legenda do Arquivo (opcional)</label>
            <vs-input v-model="form.imageCaption" class="w-full" />
          </div>
        </div>
      </div>
    </div>

    <div class="flow-block flow-block--step mt-4" data-step="1" v-else>
      <h4 class="flow-title">1. Conteúdo via fluxo</h4>
      <p class="flow-subtitle mb-0">
        O envio por fluxo está ativo. O conteúdo textual será definido pelo fluxo selecionado na etapa anterior.
      </p>
    </div>

    <div class="flow-block flow-block--step flow-block--optional mt-4" data-step="2">
      <h4 class="flow-title">2. Janela de envio (opcional)</h4>
      <p class="flow-subtitle">Preencha início e fim para limitar horário diário de disparo.</p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Início (opcional)</label>
          <flat-pickr v-model="form.startAt" class="w-full" :config="dateTimePickerConfig" />
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Fim (opcional)</label>
          <flat-pickr v-model="form.finishAt" class="w-full" :config="dateTimePickerConfig" />
        </div>
      </div>
    </div>

    <div class="flow-block flow-block--step mt-4" data-step="3" v-if="isOfficialInstance">
      <h4 class="flow-title">3. Parâmetros do Modelo de Mensagem</h4>
      <p class="flow-subtitle">Mapeamento definido na etapa anterior.</p>

      <div class="vx-row">
        <div class="vx-col w-full mb-4">
          <div v-if="requiredTemplateParamsCount > 0">
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <vs-chip
                :key="`official-param-readonly-${index}`"
                :color="officialParamsValues[index - 1] ? 'success' : 'warning'"
                v-for="index in requiredTemplateParamsCount"
              >
                {{ formatTemplatePlaceholder(index) }} -> {{ officialParamsValues[index - 1] || 'não mapeado' }}
              </vs-chip>
            </div>
          </div>
          <p v-else class="field-hint mb-0">Modelo sem variáveis obrigatórias.</p>
        </div>

        <div class="vx-col w-full mb-2" v-if="templateHasCarousel">
          <p class="field-hint text-danger mb-0">Modelo com carrossel ainda não é suportado na criação em massa.</p>
        </div>
      </div>
    </div>

    <div class="flow-block flow-block--step mt-4" data-step="4">
      <h4 class="flow-title">4. Opções adicionais</h4>
      <div class="vx-row mt-2">
        <div class="vx-col w-full md:w-1/3 mb-3" v-if="!form.useChatFlow">
          <vs-checkbox v-model="form.sendOptOut">Enviar Opt-Out</vs-checkbox>
        </div>
        <div class="vx-col w-full md:w-1/3 mb-3" v-if="!form.useChatFlow">
          <vs-checkbox v-model="form.setCaption">Enviar nome do arquivo como legenda?</vs-checkbox>
        </div>
        <div class="vx-col w-full md:w-1/3 mb-3" v-if="!form.useChatFlow">
          <vs-checkbox v-model="form.isHumanized">Ativar mensagem humanizada</vs-checkbox>
        </div>
      </div>
    </div>

    <div class="flow-block flow-block--step flow-block--final mt-4" data-step="5">
      <h4 class="flow-title">5. Validar e executar</h4>
      <p class="flow-subtitle mb-0">1) rode a Pré-validação, 2) revise o resultado, 3) execute o lote.</p>
      <div class="action-sequence mt-3">
        <div class="action-sequence-item" :class="{ done: !!dryRunBatch }">
          <span class="action-sequence-index">1</span>
          <span>Executar pré-validação</span>
        </div>
        <div class="action-sequence-item" :class="{ active: !!dryRunBatch }">
          <span class="action-sequence-index">2</span>
          <span>Revisar pré-visualização</span>
        </div>
        <div class="action-sequence-item" :class="{ ready: canExecuteBatch }">
          <span class="action-sequence-index">3</span>
          <span>Executar lote</span>
        </div>
      </div>
      <div class="action-buttons mt-4">
        <vs-button color="primary" @click="$emit('run-dry-run')" :disabled="loadingDryRun || !canRunDryRun">
          Validar campanha
        </vs-button>
        <vs-button color="success" @click="$emit('execute-batch')" :disabled="!dryRunBatch || !canExecuteBatch || loadingExecute">
          Executar Lote
        </vs-button>
      </div>
    </div>

    <div class="mt-6 dry-run-preview" v-if="dryRunBatch">
      <h4 class="mb-2">Pré-visualização da validação (Lote #{{ dryRunBatch.id }})</h4>
      <div class="mb-2 dry-run-summary">
        <vs-chip :color="statusColor(dryRunBatch.status)">{{ dryRunBatch.status }}</vs-chip>
        <span class="ml-2 text-small">Prontos: {{ dryRunBatch.ready_tenants }} / Total: {{ dryRunBatch.total_tenants }}</span>
      </div>

      <vs-table
        class="dry-run-table"
        color="dark"
        :data="dryRunItems"
        max-items="10"
        pagination
      >
        <template slot="thead">
          <vs-th>Tenant</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>IDs Resolvidos</vs-th>
          <vs-th>Erro</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :key="row.id" v-for="row in data">
              <vs-td>{{ row.tenant_name }}</vs-td>
              <vs-td>
                <vs-chip :color="statusColor(row.status)">{{ row.status }}</vs-chip>
              </vs-td>
              <vs-td>
                <div class="resolved-grid" v-if="row.resolved_ids">
                  <span
                    :key="`${row.id || row.tenant_name || 'tenant'}-${entry.key}`"
                    class="resolved-pill"
                    v-for="entry in formatResolvedIdsList(row.resolved_ids)"
                  >
                    <strong>{{ entry.label }}:</strong> {{ entry.value }}
                  </span>
                </div>
                <span class="text-small" v-else>-</span>
              </vs-td>
              <vs-td>
                <div class="dry-run-error-cell" v-if="row._errorInfo && row._errorInfo.hasError">
                  <div class="dry-run-error-line">
                    <vs-button
                      size="small"
                      color="danger"
                      type="border"
                      icon-pack="feather"
                      :icon="isDryRunErrorDetailOpen(row) ? 'icon-chevron-up' : 'icon-eye'"
                      :title="isDryRunErrorDetailOpen(row) ? 'Ocultar detalhe' : 'Ver detalhe'"
                      class="dry-run-error-toggle"
                      v-if="row._errorInfo.detail"
                      @click="toggleDryRunErrorDetail(row)"
                    />
                    <p class="mb-0 dry-run-error-summary text-danger">{{ row._errorInfo.summary }}</p>
                  </div>
                  <pre class="dry-run-error-detail mt-2" v-if="row._errorInfo.detail && isDryRunErrorDetailOpen(row)">{{ row._errorInfo.detail }}</pre>
                </div>
                <span v-else>-</span>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component';
import Upload from '@/components/upload/Upload';
import {
  detectMediaFormatFromUrl,
  formatTemplatePlaceholder,
  massCampaignStatusColor,
  parseMassCampaignError,
} from '@/utils';
import 'flatpickr/dist/flatpickr.css';

export default {
  name: 'MassCampaignStepThree',
  components: {
    flatPickr,
    Upload,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    isOfficialInstance: {
      type: Boolean,
      required: true,
    },
    requiredTemplateParamsCount: {
      type: Number,
      required: true,
    },
    requiredTemplateHeaderFormat: {
      type: String,
      default: null,
    },
    uploadApiUrl: {
      type: String,
      required: true,
    },
    uploadHeaders: {
      type: Object,
      default: () => ({}),
    },
    uploadData: {
      type: Object,
      default: () => ({}),
    },
    templateHasCarousel: {
      type: Boolean,
      required: true,
    },
    officialParamsValues: {
      type: Array,
      required: true,
    },
    dateTimePickerConfig: {
      type: Object,
      required: true,
    },
    loadingDryRun: {
      type: Boolean,
      required: true,
    },
    loadingExecute: {
      type: Boolean,
      required: true,
    },
    canExecuteBatch: {
      type: Boolean,
      required: true,
    },
    dryRunBatch: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      expandedDryRunErrorRows: {},
    };
  },
  computed: {
    isNonOfficialWithoutFlow() {
      return !this.isOfficialInstance && !this.form.useChatFlow;
    },
    trimmedMessage() {
      return `${this.form.message || ''}`.trim();
    },
    trimmedFirstMessage() {
      return `${this.form.firstMessage || ''}`.trim();
    },
    trimmedImageUrl() {
      return `${this.form.imageUrl || ''}`.trim();
    },
    currentImageFormat() {
      const explicit = `${this.form.imageType || ''}`.trim().toUpperCase();
      if (explicit !== '') { return explicit; }
      return detectMediaFormatFromUrl(this.trimmedImageUrl);
    },
    mediaUploadRequired() {
      const headerFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      return this.isOfficialInstance && headerFormat !== '' && headerFormat !== 'TEXT';
    },
    isHeaderMediaIncompatible() {
      if (!this.mediaUploadRequired || !this.trimmedImageUrl) {
        return false;
      }

      const requiredFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      if (requiredFormat === '') {
        return false;
      }

      return this.currentImageFormat !== null && this.currentImageFormat !== requiredFormat;
    },
    uploadAccept() {
      const headerFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      if (headerFormat === 'IMAGE') {
        return '.jpg,.jpeg,.png,.gif,.webp,.bmp,.jfif,.tif,.tiff';
      }

      if (headerFormat === 'VIDEO') {
        return '.mp4,.mov,.avi,.mkv,.webm,.mpeg,.mpg,.3gp';
      }

      if (headerFormat === 'DOCUMENT') {
        return '.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx';
      }

      return '.jpg,.jpeg,.png,.gif,.webp,.bmp,.jfif,.tif,.tiff,.mp4,.mov,.avi,.mkv,.webm,.mpeg,.mpg,.3gp,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx';
    },
    uploadButtonText() {
      const headerFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      if (headerFormat === 'IMAGE') {
        return 'Selecione uma imagem';
      }

      if (headerFormat === 'VIDEO') {
        return 'Selecione um vídeo';
      }

      if (headerFormat === 'DOCUMENT') {
        return 'Selecione um documento';
      }

      return 'Escolher arquivo';
    },
    dryRunBlockingReasons() {
      const reasons = [];
      const hasMessage = this.trimmedMessage !== '';
      const hasFirstMessage = this.trimmedFirstMessage !== '';
      const hasImageUrl = this.trimmedImageUrl !== '';

      if (this.templateHasCarousel && this.isOfficialInstance) {
        reasons.push('Modelo com carrossel ainda não é suportado para criação em massa.');
      }

      if (this.isOfficialInstance && this.requiredTemplateParamsCount > 0) {
        const values = Array.isArray(this.officialParamsValues) ? this.officialParamsValues : [];
        const missingByCount = values.length !== this.requiredTemplateParamsCount;
        const hasEmptyValue = values.some((item) => `${item || ''}`.trim() === '');

        if (missingByCount || hasEmptyValue) {
          reasons.push(`Mapeie os ${this.requiredTemplateParamsCount} parâmetros do modelo para campos do grupo.`);
        }
      }

      const headerFormat = `${this.requiredTemplateHeaderFormat || ''}`.trim().toUpperCase();
      if (this.isOfficialInstance && headerFormat !== '' && headerFormat !== 'TEXT') {
        if (!hasImageUrl) {
          reasons.push(`O modelo selecionado exige mídia no cabeçalho (${headerFormat}). Faça upload de um arquivo.`);
        } else {
          const providedFormat = this.currentImageFormat;
          if (!providedFormat) {
            reasons.push('Não foi possível identificar o tipo da mídia informada para o cabeçalho do modelo.');
          } else if (providedFormat !== headerFormat) {
            reasons.push(`A mídia informada é ${providedFormat}, porém o modelo exige ${headerFormat}.`);
          }
        }
      }

      if (!this.form.useChatFlow && !this.isOfficialInstance && !hasMessage && !hasImageUrl) {
        reasons.push('Para conexão web sem fluxo, informe mensagem ou mídia.');
      }

      if (this.form.isHumanized && this.isOfficialInstance && !hasMessage && !hasImageUrl) {
        reasons.push('Na humanização Cloud, informe mensagem ou mídia.');
      }

      if (this.form.isHumanized && !this.isOfficialInstance) {
        if (!hasFirstMessage) {
          reasons.push('Na humanização web, preencha a primeira mensagem.');
        }

        if (!hasMessage && !hasImageUrl) {
          reasons.push('Na humanização web, informe mensagem ou mídia.');
        }
      }

      return reasons;
    },
    canRunDryRun() {
      return this.dryRunBlockingReasons.length === 0;
    },
    dryRunItems() {
      const items = this.dryRunBatch && Array.isArray(this.dryRunBatch.items)
        ? this.dryRunBatch.items
        : [];

      return items.map((item, index) => {
        const row = Object.assign({}, item || {});
        const rowId = row.id === null || row.id === undefined
          ? `${row.tenant_name || 'tenant'}-${index}`
          : `${row.id}`;

        row._errorRowKey = `${rowId}-${index}`;
        row._errorInfo = parseMassCampaignError(row.error_message);
        return row;
      });
    },
  },
  methods: {
    formatTemplatePlaceholder,
    statusColor: massCampaignStatusColor,
    successUploadMedia(response) {
      const message = response && response.data && response.data.message ? response.data.message : 'Arquivo carregado com sucesso.';

      const fileUrl = `${(response && response.data && response.data.fileUrl) || ''}`.trim();
      if (fileUrl !== '') {
        this.form.imageUrl = fileUrl;
      }

      const fileType = `${(response && response.data && response.data.fileType) || ''}`.trim().toUpperCase();
      this.form.imageType = fileType || detectMediaFormatFromUrl(this.form.imageUrl);

      const lastSource = response && Array.isArray(response.srcs) && response.srcs.length  ? response.srcs[response.srcs.length - 1]  : null;

      if (lastSource && lastSource.name) {
        this.form.imageCaption = lastSource.name;
      }

      this.$vs.notify({
        color: 'success',
        title: 'Upload concluído',
        text: message,
      });
    },
    errorUploadMedia(response) {
      const message = response && response.data && response.data.message
        ? response.data.message
        : 'Falha ao subir arquivo.';

      this.$vs.notify({
        color: 'danger',
        title: 'Erro no upload',
        text: message,
      });
    },
    deleteUploadMedia() {
      this.form.imageUrl = '';
      this.form.imageType = null;
      this.form.imageCaption = '';
      this.form.imageDescription = '';
    },
    async removeUploadedMedia() {
      const uploadRef = this.$refs.mediaUpload;
      if (uploadRef && typeof uploadRef.removeFile === 'function') { await uploadRef.removeFile(0, true); }
      this.deleteUploadMedia();
    },
    formatResolvedIdsList(resolved) {
      if (!resolved) {
        return [];
      }

      const keys = [
        ['instance_id', 'Instância'],
        ['customers_group_id', 'Grupo'],
        ['message_template_id', 'Modelo'],
        ['chat_flow_id', 'Fluxo'],
        ['instance_is_official', 'Conexão Cloud'],
        ['template_header_format', 'Header'],
      ];

      return keys.map(([key, label]) => {
        const value = resolved[key];
        if (value === null || value === undefined || value === '') {
          return { key, label, value: '-' };
        }

        if (typeof value === 'boolean') {
          return { key, label, value: value ? 'sim' : 'não' };
        }

        return { key, label, value: `${value}` };
      });
    },
    formatResolvedIds(resolved) {
      if (!resolved) {
        return '-';
      }

      const keys = [
        'instance_id',
        'customers_group_id',
        'message_template_id',
        'chat_flow_id',
        'instance_is_official',
        'template_header_format',
      ];

      return keys.map((key) => {
        const value = resolved[key];
        if (value === null || value === undefined || value === '') {
          return `${key}: -`;
        }

        if (typeof value === 'boolean') {
          return `${key}: ${value ? 'sim' : 'não'}`;
        }

        return `${key}: ${value}`;
      }).join(' | ');
    },
    isDryRunErrorDetailOpen(row) {
      const rowKey = row && row._errorRowKey ? row._errorRowKey : null;
      if (!rowKey) {
        return false;
      }

      return !!this.expandedDryRunErrorRows[rowKey];
    },
    toggleDryRunErrorDetail(row) {
      const rowKey = row && row._errorRowKey ? row._errorRowKey : null;
      if (!rowKey) {
        return;
      }

      this.$set(this.expandedDryRunErrorRows, rowKey, !this.expandedDryRunErrorRows[rowKey]);
    },
  },
  watch: {
    dryRunBatch() {
      this.expandedDryRunErrorRows = {};
    },
  },
};
</script>

<style lang="scss" scoped>
.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.field-hint {
  font-size: 12px;
  color: #6b7280;
}

.flow-block {
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  padding: 14px;
  background: #fff;
}

.flow-block--intro {
  background: #f8fafc;
  border-color: #dbe4f0;
}

.flow-block--step {
  position: relative;
  padding-left: 52px;
}

.flow-block--step::before {
  content: attr(data-step);
  position: absolute;
  left: 14px;
  top: 14px;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #eef2f7;
  color: #334155;
}

.flow-block--optional::before {
  background: #eceff3;
  color: #4b5563;
}

.flow-block--final {
  border-color: #d6dde7;
  background: #f9fafb;
}

.flow-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
}

.flow-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 10px;
}

.step-sequence {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.step-sequence-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
}

.step-sequence-index {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #334155;
  font-size: 10px;
  font-weight: 700;
}

.validation-box {
  border: 1px solid #f59e0b;
  background: #fffbeb;
  border-radius: 6px;
  padding: 10px;
}

.validation-title {
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  margin-bottom: 4px;
}

.validation-list {
  margin: 0;
  padding-left: 18px;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
}

.uploaded-media-info {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.media-remove-button {
  min-width: 34px;
}

.action-sequence {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-sequence-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #6b7280;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
}

.action-sequence-item.done {
  border-color: #86efac;
  background: #f0fdf4;
  color: #166534;
}

.action-sequence-item.active {
  border-color: #f3d7a9;
  background: #fff8eb;
  color: #9a6700;
}

.action-sequence-item.ready {
  border-color: #6ee7b7;
  background: #ecfdf5;
  color: #047857;
}

.action-sequence-index {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
  font-size: 10px;
  font-weight: 700;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.dry-run-preview {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 12px;
}

.dry-run-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  border-bottom: 1px solid #edf2f7;
}

.dry-run-table {
  margin-top: 8px;
}

.dry-run-table ::v-deep .vs-con-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
}

.dry-run-table ::v-deep .vs-table--thead th {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 12px;
}

.dry-run-table ::v-deep .vs-table--thead .vs-table-text {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.dry-run-table ::v-deep .vs-table--tbody-table .tr-values td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.dry-run-table ::v-deep .vs-table--tbody-table .tr-values:last-child td {
  border-bottom: 0;
}

.dry-run-table ::v-deep .vs-table--pagination {
  border-top: 1px solid #eef2f7;
  background: #fcfcfd;
}

.resolved-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.resolved-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  background: #f8fafc;
  padding: 3px 8px;
  font-size: 11px;
  color: #475569;
}

.dry-run-error-cell {
  display: block;
  max-width: 100%;
}

.dry-run-error-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.dry-run-error-summary {
  margin: 0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
  flex: 1;
}

.dry-run-error-toggle {
  min-height: 28px;
}

.dry-run-error-detail {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 11px;
  line-height: 1.45;
  color: #334155;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  background: #fff7f7;
  padding: 8px;
}

.media-preview {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px;
  max-width: 320px;
}

.media-preview img,
.media-preview video {
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  border-radius: 4px;
}

.message-textarea ::v-deep .vs-textarea {
  min-height: 220px;
  resize: vertical !important;
}

@media (max-width: 768px) {
  .flow-block--step {
    padding-left: 14px;
    padding-top: 46px;
  }

  .flow-block--step::before {
    top: 12px;
    left: 14px;
  }
}
</style>
